"use client";

import { useEffect, useState } from "react";
import { Sun, Moon, Flame } from "lucide-react";

type Theme = "light" | "dark" | "color";

const themes: { value: Theme; icon: typeof Sun; label: string }[] = [
  { value: "light", icon: Sun, label: "Light" },
  { value: "dark", icon: Moon, label: "Dark" },
  { value: "color", icon: Flame, label: "Color" },
];

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem("theme") as Theme | null;
  if (stored && ["light", "dark", "color"].includes(stored)) return stored;
  if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

export function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const initial = getInitialTheme();
    setTheme(initial);
    document.documentElement.dataset.theme = initial;
  }, []);

  function switchTheme(next: Theme) {
    setTheme(next);
    document.documentElement.dataset.theme = next;
    localStorage.setItem("theme", next);
  }

  if (!mounted) {
    return <div className="flex gap-1" aria-hidden />;
  }

  return (
    <div className="flex gap-1">
      {themes.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          onClick={() => switchTheme(value)}
          className={`rounded-md p-1.5 transition-colors ${
            theme === value
              ? "bg-theme-accent text-theme-accent-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-label={`${label} theme`}
          title={label}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}
